// Chemin: C:\gestion-copro\src\services\log.service.ts
// Service de traçabilité des actions - Copro Protect
// Session 40 : création initiale - module Communications

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import {
  Log,
  LogCreateData,
  CategorieLog,
  NiveauLog,
  getNiveauAction
} from '../types/log.types';

const COLLECTION_NAME = 'logs';

// ============================================================================
// CRÉATION DE LOGS
// ============================================================================

/**
 * Crée une entrée de log
 */
export async function createLog(data: LogCreateData): Promise<string> {
  const now = Timestamp.now();
  
  // Déterminer le niveau automatiquement si non spécifié
  const niveau = data.niveau || getNiveauAction(data.action);
  
  const log: Omit<Log, 'id'> = {
    coproprieteId: data.coproprieteId,
    categorie: data.categorie,
    action: data.action,
    niveau,
    acteurId: data.acteurId,
    acteurNom: data.acteurNom,
    acteurIp: data.acteurIp,
    cibleType: data.cibleType,
    cibleId: data.cibleId,
    cibleLibelle: data.cibleLibelle,
    description: data.description,
    details: data.details,
    date: now
  };
  
  // Nettoyer les champs undefined
  const logClean: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(log)) {
    if (value !== undefined) {
      logClean[key] = value;
    }
  }
  
  const docRef = await addDoc(collection(db, COLLECTION_NAME), logClean);
  return docRef.id;
}

/**
 * Crée un log système (sans acteur humain)
 */
export async function createSystemLog(
  coproprieteId: string,
  action: LogCreateData['action'],
  description: string,
  cibleType?: Log['cibleType'],
  cibleId?: string,
  cibleLibelle?: string,
  details?: Record<string, unknown>
): Promise<string> {
  return createLog({
    coproprieteId,
    categorie: 'systeme',
    action,
    acteurId: null,
    acteurNom: 'Système',
    description,
    cibleType,
    cibleId,
    cibleLibelle,
    details
  });
}

// ============================================================================
// LECTURE DE LOGS
// ============================================================================

/**
 * Récupère un log par son ID
 */
export async function getLogById(id: string): Promise<Log | null> {
  const docRef = doc(db, COLLECTION_NAME, id);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists()) {
    return null;
  }
  
  return {
    id: snapshot.id,
    ...snapshot.data()
  } as Log;
}

/**
 * Récupère les logs d'une copropriété
 */
export async function getLogsByCopropriete(
  coproprieteId: string,
  options?: {
    categorie?: CategorieLog;
    niveauMin?: NiveauLog;
    limit?: number;
  }
): Promise<Log[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('coproprieteId', '==', coproprieteId),
    orderBy('date', 'desc'),
    limit(options?.limit || 100)
  );
  
  const snapshot = await getDocs(q);
  let logs = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Log[];
  
  // Filtrage côté client (pour éviter les index composites complexes)
  if (options?.categorie) {
    logs = logs.filter(l => l.categorie === options.categorie);
  }
  
  if (options?.niveauMin) {
    const niveaux: NiveauLog[] = ['info', 'important', 'critique'];
    const indexMin = niveaux.indexOf(options.niveauMin);
    logs = logs.filter(l => niveaux.indexOf(l.niveau) >= indexMin);
  }
  
  return logs;
}

/**
 * Récupère les logs d'une entité spécifique (courrier, affaire, etc.)
 */
export async function getLogsByCible(
  cibleType: Log['cibleType'],
  cibleId: string
): Promise<Log[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('cibleType', '==', cibleType),
    where('cibleId', '==', cibleId),
    orderBy('date', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Log[];
}

/**
 * Récupère les logs d'un utilisateur
 */
export async function getLogsByActeur(
  acteurId: string,
  coproprieteId?: string
): Promise<Log[]> {
  let q;
  
  if (coproprieteId) {
    q = query(
      collection(db, COLLECTION_NAME),
      where('acteurId', '==', acteurId),
      where('coproprieteId', '==', coproprieteId),
      orderBy('date', 'desc'),
      limit(100)
    );
  } else {
    q = query(
      collection(db, COLLECTION_NAME),
      where('acteurId', '==', acteurId),
      orderBy('date', 'desc'),
      limit(100)
    );
  }
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Log[];
}

/**
 * Récupère les logs importants et critiques récents
 */
export async function getLogsImportants(
  coproprieteId: string,
  nombreJours: number = 7
): Promise<Log[]> {
  const dateLimit = new Date();
  dateLimit.setDate(dateLimit.getDate() - nombreJours);
  
  const logs = await getLogsByCopropriete(coproprieteId, {
    niveauMin: 'important',
    limit: 50
  });
  
  return logs.filter(l => l.date.toDate() >= dateLimit);
}

// ============================================================================
// STATISTIQUES
// ============================================================================

/**
 * Compte les logs par catégorie
 */
export async function countLogsByCategorie(
  coproprieteId: string
): Promise<Record<CategorieLog, number>> {
  const logs = await getLogsByCopropriete(coproprieteId, { limit: 1000 });
  
  const counts: Record<CategorieLog, number> = {
    onboarding: 0,
    courrier: 0,
    email: 0,
    document: 0,
    affaire: 0,
    signalement: 0,
    utilisateur: 0,
    systeme: 0
  };
  
  for (const log of logs) {
    counts[log.categorie]++;
  }
  
  return counts;
}

/**
 * Récupère l'activité récente (pour le dashboard)
 */
export async function getActiviteRecente(
  coproprieteId: string,
  nombreItems: number = 10
): Promise<Log[]> {
  return getLogsByCopropriete(coproprieteId, { limit: nombreItems });
}

// ============================================================================
// HELPERS POUR LOGS COURANTS
// ============================================================================

/**
 * Log de connexion utilisateur
 */
export async function logConnexion(
  coproprieteId: string,
  userId: string,
  userNom: string,
  ip?: string
): Promise<void> {
  await createLog({
    coproprieteId,
    categorie: 'utilisateur',
    action: 'utilisateur_connecte',
    acteurId: userId,
    acteurNom: userNom,
    acteurIp: ip,
    description: `${userNom} s'est connecté`
  });
}

/**
 * Log de déconnexion utilisateur
 */
export async function logDeconnexion(
  coproprieteId: string,
  userId: string,
  userNom: string
): Promise<void> {
  await createLog({
    coproprieteId,
    categorie: 'utilisateur',
    action: 'utilisateur_deconnecte',
    acteurId: userId,
    acteurNom: userNom,
    description: `${userNom} s'est déconnecté`
  });
}

/**
 * Log d'upload de document
 */
export async function logUploadDocument(
  coproprieteId: string,
  userId: string,
  userNom: string,
  documentId: string,
  documentTitre: string
): Promise<void> {
  await createLog({
    coproprieteId,
    categorie: 'document',
    action: 'document_uploade',
    acteurId: userId,
    acteurNom: userNom,
    cibleType: 'document',
    cibleId: documentId,
    cibleLibelle: documentTitre,
    description: `Document "${documentTitre}" uploadé`
  });
}

/**
 * Log de suppression de document
 */
export async function logSuppressionDocument(
  coproprieteId: string,
  userId: string,
  userNom: string,
  documentId: string,
  documentTitre: string
): Promise<void> {
  await createLog({
    coproprieteId,
    categorie: 'document',
    action: 'document_supprime',
    niveau: 'important',
    acteurId: userId,
    acteurNom: userNom,
    cibleType: 'document',
    cibleId: documentId,
    cibleLibelle: documentTitre,
    description: `Document "${documentTitre}" supprimé`
  });
}

/**
 * Log d'archivage email
 */
export async function logArchivageEmail(
  coproprieteId: string,
  userId: string,
  userNom: string,
  emailId: string,
  emailSujet: string
): Promise<void> {
  await createLog({
    coproprieteId,
    categorie: 'email',
    action: 'email_archive',
    acteurId: userId,
    acteurNom: userNom,
    cibleType: 'email',
    cibleId: emailId,
    cibleLibelle: emailSujet,
    description: `Email "${emailSujet}" archivé`
  });
}
